import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

const MAX_NAME_LENGTH = 100;

interface NameModalProps {
    isOpen: boolean;
    initialName: string;
    onClose: () => void;
    onSave: (fullname: string) => Promise<void> | void;
}

const NameModal: React.FC<NameModalProps> = ({ isOpen, initialName, onClose, onSave }) => {
    // Defensive trim: legacy DB rows could have leading/trailing whitespace,
    // and we don't want canSave to mistake a whitespace-only fix for a no-op.
    const initialTrimmed = initialName.trim();
    const [value, setValue] = useState(initialTrimmed);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setValue(initialTrimmed);
            setSaving(false);
        }
    }, [isOpen, initialTrimmed]);

    if (!isOpen) return null;

    const trimmed = value.trim();
    const canSave = !!trimmed && trimmed !== initialTrimmed && trimmed.length <= MAX_NAME_LENGTH;

    const submit = async () => {
        if (!canSave || saving) return;
        setSaving(true);
        try {
            await onSave(trimmed);
        } finally {
            setSaving(false);
        }
    };

    const modalContent = (
        <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-lg shadow-xl w-full max-w-[520px] p-6 relative"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-gray-900">Edit name</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-800">
                        <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                </div>

                <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-semibold text-gray-700">Full name</label>
                        <input
                            type="text"
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') submit();
                            }}
                            maxLength={MAX_NAME_LENGTH}
                            autoFocus
                            disabled={saving}
                            placeholder="Enter your full name"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
                        />
                        <p className="text-xs text-gray-400 mt-1">
                            {value.length}/{MAX_NAME_LENGTH}
                        </p>
                    </div>
                </div>

                <div className="flex justify-end items-center gap-3 mt-6">
                    <button
                        onClick={onClose}
                        disabled={saving}
                        className="px-4 py-2 text-sm font-semibold text-gray-800 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        disabled={!canSave || saving}
                        onClick={submit}
                        className={`px-4 py-2 text-sm font-bold text-white rounded-lg inline-flex items-center gap-2 ${
                            canSave && !saving
                                ? 'bg-gray-800 hover:bg-gray-700'
                                : 'bg-gray-300 cursor-not-allowed'
                        }`}
                    >
                        {saving && (
                            <span className="w-3.5 h-3.5 rounded-full border-2 border-white border-t-transparent animate-spin" />
                        )}
                        Save
                    </button>
                </div>
            </div>
        </div>
    );

    const modalRoot = document.getElementById('modal-root');
    return modalRoot ? ReactDOM.createPortal(modalContent, modalRoot) : null;
};

export default NameModal;
